import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PricingSelection } from './PricingSelection';
import { WebPricingSelection } from './WebPricingSelection';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { useRevenueCat } from '@/hooks/useRevenueCat';
import { useToast } from '@/hooks/use-toast';

interface SubscriptionGateProps {
  children: React.ReactNode;
}

export const SubscriptionGate = ({ children }: SubscriptionGateProps) => {
  const { user, loading, subscribed, inTrial, subscriptionEnd, checkSubscription, signOut } = useAuth();
  const { isIOS, customerInfo, restorePurchases, isConfigured } = useRevenueCat();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const didInitialCheckRef = useRef(false);
  // Lets expired-trial users back into the app in degraded mode (no translation).
  // Cleared in AuthContext.signOut so the flag never bleeds across different accounts.
  const [dismissedPaywall, setDismissedPaywall] = useState(
    () => localStorage.getItem('gcc_dismissed_paywall') === 'true'
  );

  // Memoize to avoid calling functions during render that cause re-render loops
  const hasLocalIOSAccess = useMemo(() => {
    if (!isIOS || !isConfigured || !customerInfo) return false;
    return customerInfo.entitlements?.active?.['Global Chat Connect Pro']?.isActive === true;
  }, [isIOS, isConfigured, customerInfo]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // Check subscription once when user is available
  useEffect(() => {
    if (loading || !user) return;
    if (didInitialCheckRef.current) return;

    didInitialCheckRef.current = true;
    setIsCheckingSubscription(true);
    
    // Check if we're returning from Stripe checkout
    const params = new URLSearchParams(window.location.search);
    const isCheckoutReturn = params.get('checkout') === 'success';
    
    // Never block the app on this check: if it stalls, render anyway.
    const watchdog = setTimeout(() => setIsCheckingSubscription(false), 12000);

    let started = false;
    // Use a small delay to ensure auth state is fully settled
    const timer = setTimeout(() => {
      started = true;
      checkSubscription().finally(() => {
        clearTimeout(watchdog);
        setIsCheckingSubscription(false);
        if (isCheckoutReturn) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      });
    }, 100);

    return () => {
      if (!started) {
        // Check was cancelled before it ran: allow the next run to retry,
        // otherwise the spinner would never clear.
        clearTimeout(timer);
        clearTimeout(watchdog);
        didInitialCheckRef.current = false;
        setIsCheckingSubscription(false);
      }
    };
    // Depend on the user id (not object identity) so duplicate setUser calls don't cancel the check.
  }, [loading, user?.id]); // Intentionally omit checkSubscription to prevent loops

  // iOS: if RevenueCat shows an active entitlement, sync in background (only once)
  const didSyncIOSRef = useRef(false);
  useEffect(() => {
    if (hasLocalIOSAccess && user && !subscribed && !inTrial && !didSyncIOSRef.current) {
      didSyncIOSRef.current = true;
      checkSubscription();
    }
  }, [hasLocalIOSAccess, user, subscribed, inTrial]);

  // Detect if user has active RevenueCat entitlements but local state shows unsubscribed
  const didShowRestoreRef = useRef(false);
  useEffect(() => {
    if (didShowRestoreRef.current) return;
    if (isIOS && isConfigured && customerInfo && !subscribed && !inTrial && !loading && !isCheckingSubscription) {
      const hasRevenueCatEntitlement = customerInfo?.entitlements?.active?.['Global Chat Connect Pro']?.isActive;
      if (hasRevenueCatEntitlement) {
        didShowRestoreRef.current = true;
        console.log('[SubscriptionGate] Detected active RevenueCat entitlement but local state is unsubscribed');
        setShowRestorePrompt(true);
      }
    }
  }, [isIOS, isConfigured, customerInfo, subscribed, inTrial, loading, isCheckingSubscription]);

  const handleRestorePurchases = async () => {
    setIsRestoring(true);
    try {
      await restorePurchases();
      toast({
        title: "Purchases Restored",
        description: "Checking your subscription status...",
      });

      // Wait for RevenueCat to sync, then check subscription
      setTimeout(async () => {
        await checkSubscription();
        setShowRestorePrompt(false);
        // Reload to ensure state is fully updated
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('[SubscriptionGate] Restore error:', error);
      toast({
        title: "Restore Failed",
        description: "Could not restore purchases. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleRefreshSubscription = async () => {
    setIsRefreshing(true);
    try {
      await checkSubscription();
      toast({
        title: "Refreshed",
        description: subscriptionEnd
          ? `Checked access. Subscription end: ${new Date(subscriptionEnd).toLocaleString()}`
          : "Checked access.",
      });
    } catch (e) {
      toast({
        title: "Refresh failed",
        description: "Could not check your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Show loading while checking subscription on initial load
  if (loading || isCheckingSubscription) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Allow access if subscribed, in trial, has local iOS access,
  // or user has chosen to continue in degraded mode (no translation).
  if (subscribed || inTrial || hasLocalIOSAccess || dismissedPaywall) {
    return <>{children}</>;
  }

  // User is not subscribed - show paywall
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (e) {
      console.error('[SubscriptionGate] signOut error:', e);
      navigate('/auth');
    }
  };

  return (
    <div className="safe-area-screen bg-background overflow-y-auto">
      <div className="flex flex-col items-center p-4 px-[max(env(safe-area-inset-left),1rem)] pr-[max(env(safe-area-inset-right),1rem)] pt-[max(env(safe-area-inset-top),1rem)] pb-[max(env(safe-area-inset-bottom),1rem)]">
        <div className="max-w-md w-full mb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="px-0 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            <span className="text-sm font-medium">Sign out</span>
          </Button>
        </div>

        {/* Restore Purchases Prompt */}
        {showRestorePrompt && isIOS && (
          <Card className="max-w-md w-full mb-4 border-primary">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Existing Subscription Found
              </CardTitle>
              <CardDescription>
                We detected an active subscription on your account. Tap below to restore it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleRestorePurchases}
                disabled={isRestoring}
                className="w-full"
              >
                {isRestoring && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Restore Purchases
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Subscribe to Global Chat Connect</CardTitle>
            <CardDescription>
              Get unlimited access to auto-translated text and voice messages!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isIOS ? <PricingSelection /> : <WebPricingSelection />}
            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem('gcc_dismissed_paywall', 'true');
                  setDismissedPaywall(true);
                }}
                className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
              >
                {t('continueWithoutTranslation')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
